import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { keyframes } from "@mui/system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
// dialogConstants.js (or export from DialogContext.js)
export const DIALOG_TYPES = {
  // Messages (1 Button, Centered Layout)
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
  INFO: "INFO",

  // Confirmations (2 Buttons, Inline Layout)
  CONFIRM_NORMAL: "CONFIRM_NORMAL",
  CONFIRM_WARNING: "CONFIRM_WARNING",
  CONFIRM_DELETE: "CONFIRM_DELETE",
};
// --- Animations ---
const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25%, 75% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// --- Configuration Map ---
// Maps each dialog type to its specific UI properties
const DIALOG_CONFIG = {
  [DIALOG_TYPES.SUCCESS]: {
    color: "success.main",
    Icon: CheckCircleOutlineIcon,
    animation: `${bounceIn} 0.6s ease-out`,
    layout: "centered",
    showCancel: false,
  },
  [DIALOG_TYPES.ERROR]: {
    color: "error.main",
    Icon: ErrorOutlineIcon,
    animation: `${shake} 0.5s ease-in-out`,
    layout: "centered",
    showCancel: false,
  },
  [DIALOG_TYPES.INFO]: {
    color: "info.main",
    Icon: InfoOutlinedIcon,
    animation: `${bounceIn} 0.4s ease-out`,
    layout: "centered",
    showCancel: false,
  },
  [DIALOG_TYPES.CONFIRM_NORMAL]: {
    color: "primary.main",
    Icon: InfoOutlinedIcon,
    animation: "none",
    layout: "inline",
    showCancel: true,
    confirmVariant: "text",
  },
  [DIALOG_TYPES.CONFIRM_WARNING]: {
    color: "warning.main",
    Icon: WarningAmberIcon,
    animation: `${shake} 0.5s ease-in-out`,
    layout: "inline",
    showCancel: true,
    confirmVariant: "contained",
  },
  [DIALOG_TYPES.CONFIRM_DELETE]: {
    color: "error.main",
    Icon: DeleteIcon,
    animation: `${bounceIn} 0.4s ease-out`,
    layout: "inline",
    showCancel: true,
    confirmVariant: "contained",
  },
};

export const CommonDialog = ({ dialog, onClose }) => {
  const {
    isOpen = false,
    type = DIALOG_TYPES.INFO, // Default fallback
    title,
    message,
    buttonText = "OK",
    cancelText = "Cancel",
    onConfirm,
    disableEscapeKeyDown = false,
  } = dialog;

  // Retrieve config for the current type, fallback to INFO if undefined
  const config = DIALOG_CONFIG[type] || DIALOG_CONFIG[DIALOG_TYPES.INFO];
  const { Icon, color, animation, layout, showCancel, confirmVariant } = config;

  const handleDismiss = (event, reason) => {
    if (reason === "backdropClick" && disableEscapeKeyDown) return;
    onClose();
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDismiss}
      disableEscapeKeyDown={disableEscapeKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: "350px",
          animation: `${fadeIn} 0.3s ease-out`,
          ...(layout === "centered" && { textAlign: "center", p: 2 }),
        },
      }}
    >
      {/* HEADER & CONTENT */}
      {layout === "centered" ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}>
            <Icon sx={{ fontSize: 64, color, animation }} />
          </Box>
          {title && (
            <DialogTitle
              sx={{ color, fontSize: "1.5rem", fontWeight: "bold", pb: 1 }}
            >
              {title}
            </DialogTitle>
          )}
          <DialogContent sx={{ typography: "body1", px: 3 }}>
            {message}
          </DialogContent>
        </>
      ) : (
        <>
          {title && (
            <DialogTitle sx={{ color, fontWeight: "bold", pb: 1 }}>
              {title}
            </DialogTitle>
          )}
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: title ? 0 : 2,
              }}
            >
              <Icon sx={{ fontSize: 40, color, animation }} />
              <Box sx={{ typography: "body1" }}>{message}</Box>
            </Box>
          </DialogContent>
        </>
      )}

      {/* ACTIONS */}
      <DialogActions
        sx={{
          justifyContent: layout === "centered" ? "center" : "flex-end",
          pt: 2,
          pb: 2,
          px: 3,
        }}
      >
        {showCancel && (
          <Button onClick={handleDismiss} color="inherit">
            {cancelText}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant={confirmVariant || "contained"}
          autoFocus
          sx={{
            ...(confirmVariant === "contained" && {
              backgroundColor: color,
              color: "white",
              "&:hover": { backgroundColor: color, opacity: 0.9 },
            }),
            ...(confirmVariant === "text" && {
              color: color,
              "&:hover": { backgroundColor: `${color}10` },
            }),
            px: layout === "centered" ? 4 : 2,
            borderRadius: 2,
          }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
